import {
  createTheme,
  ComponentsOverrides,
  ComponentsProps,
  ComponentsVariants,
  Theme,
  PaletteColor,
  PaletteColorOptions,
} from '@mui/material/styles';
import { CSSObject, fontSize, textTransform } from '@mui/system';
/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
import type {} from '@mui/lab/themeAugmentation';
import { inspire, inspireMed, inspireBlack, inspireHeavy } from './fonts';
import { DataGridProps } from '@mui/x-data-grid';
import { commonTokens, darkTokens, lightTokens } from './tokens';

declare module '@mui/material/styles' {
  interface Theme {
    spacingTokens: {
      xsm: number;
      sm: number;
      md: number;
      xmd: number;
      xxmd: number;
      xlg: number;
      xxlg: number;
    };
  }
  interface ThemeOptions {
    spacingTokens?: {
      xsm?: number;
      sm?: number;
      md?: number;
      xmd?: number;
      xxmd?: number;
      xlg?: number;
      xxlg?: number;
    };
  }
  interface Components {
    MuiDataGrid?: {
      defaultProps?: Partial<DataGridProps>;
      styleOverrides?: {
        root?: CSSObject;
        columnHeaders?: CSSObject;
        columnHeader?: CSSObject;
        columnHeaderTitle?: CSSObject;
        row?: CSSObject;
        columnSeparator?: CSSObject;
        cell?: CSSObject;
        menu?: CSSObject;
        main?: CSSObject;
        footerContainer?: CSSObject;
        toolbar?: CSSObject;
        panelWrapper?: CSSObject;
      };
      variants?: Array<{
        props: Partial<DataGridProps>;
        style: CSSObject;
      }>;
    };
  }
  interface Palette {
    indigo: PaletteColor;
    changeRequested: PaletteColor;
    gray: {
      white: string;
      gray03?: string;
      gray05?: string;
      gray30: string;
    };
  }
  interface PaletteOptions {
    indigo?: PaletteColorOptions;
    changeRequested?: PaletteColorOptions;
    gray?: {
      white?: string;
      gray03?: string;
      gray05?: string;
      gray30?: string;
    };
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsSizeOverrides {
    xs: true;
  }
}

export const palette = {
  primary: {
    main: 'rgba(29, 32, 38, 1)',
    light: 'rgba(74, 76, 81, 1)',
    dark: 'rgba(20, 22, 26, 1)',
    contrastText: 'rgba(255, 255, 255, 1)',
  },
  secondary: {
    main: '#E6F0FE',
    dark: '#0C2B64',
    contrastText: 'rgba(20, 91, 184, 1)',
  },
  action: {
    active: 'rgba(29, 32, 38, 0.56)',
    hover: 'rgba(29, 32, 38, 0.04)',
    selected: 'rgba(29, 32, 38, 0.08)',
    disabledBackground: 'rgba(29, 32, 38, 0.12)',
    focus: 'rgba(29, 32, 38, 0.12)',
    disabled: 'rgba(29, 32, 38, 0.38)',
  },
  error: {
    main: 'rgba(184, 20, 62, 1)',
    light: 'rgba(198, 67, 100, 1)',
    dark: 'rgba(128, 14, 43, 1)',
    contrastText: 'rgba(255, 255, 255, 1)',
    200: '#FDB1CA',
    400: '#FB6395',
    600: '#A60E40',
    800: '#530720',
  },
  success: {
    main: 'rgba(10, 92, 51, 1)',
    light: 'rgba(59, 124, 91, 1)',
    dark: 'rgba(7, 64, 35, 1)',
    contrastText: 'rgba(255, 255, 255, 1)',
    200: '#B3F7D5',
    400: '#67EFAC',
    600: '#129A57',
    800: '#094D2B',
  },
  info: {
    main: 'rgba(20, 91, 184, 1)',
    light: 'rgba(67, 123, 198, 1)',
    dark: 'rgba(14, 63, 128, 1)',
    contrastText: 'rgba(255, 255, 255, 1)',
    200: '#AACEFD',
    400: '#559DFB',
    600: '#0048A6',
    800: '#002453',
  },
  background: {
    default: '#fff',
  },
  warning: {
    main: 'rgba(153, 102, 0, 1)',
    light: 'rgba(214, 159, 51, 1)',
    dark: 'rgba(142, 95, 0, 1)',
    contrastText: '#FFFFFF',
    200: '#FDE5AA',
    400: '#FBCB55',
    600: '#A67600',
    800: '#533B00',
  },
  text: {
    primary: 'rgba(29, 32, 38, 1)',
    secondary: 'rgba(29, 32, 38, 0.6)',
    disabled: 'rgba(29, 32, 38, 0.38)',
  },
  indigo: {
    light: '#F5E8FC',
    main: '#7D14B8',
    dark: '#5D0F8A',
    contrastText: '#FFFFFF',
  },
  gray: {
    white: '#ffffff',
    gray30: '#a8afbd',
  },
  divider: '#1d20261f',
  changeRequested: {
    main: '#0002B0',
  },
};

export const iconSizes = {
  xs: 18,
  small: 20,
  medium: 24,
  large: 35,
};

/**
 * Figma spacing tokens (ADK design system).
 * Values are MUI spacing units (8px base); use with theme.spacing(theme.spacingTokens.xsm).
 * Tokens: xsm=8px, sm=12px, md=16px, xmd=20px, xxmd=24px, xlg=32px, xxlg=40px
 */
export const spacingTokens = {
  xsm: 1,
  sm: 1.5,
  md: 2,
  xmd: 2.5,
  xxmd: 3,
  xlg: 4,
  xxlg: 5,
} as const;

/** Lookup keyed by palette `main` for extendColor/componentColors helpers. Derived from tokens.ts. */
export const componentColors: Record<string, { background: string; content: string }> = {
  [lightTokens.primary.main]: commonTokens.component.primary,
  [lightTokens.error.main]: commonTokens.component.error,
  [lightTokens.warning.main]: commonTokens.component.warning,
  [lightTokens.info.main]: commonTokens.component.info,
  [lightTokens.success.main]: commonTokens.component.success,
  [lightTokens.indigo.main]: commonTokens.component.indigo,
  [darkTokens.primary.main]: {
    background: commonTokens.componentDark.primary.background,
    content: commonTokens.componentDark.primary.content,
  },
  [darkTokens.error.main]: commonTokens.componentDark.error,
  [darkTokens.warning.main]: commonTokens.componentDark.warning,
  [darkTokens.info.main]: commonTokens.componentDark.info,
  [darkTokens.success.main]: commonTokens.componentDark.success,
  [darkTokens.indigo.main]: commonTokens.componentDark.indigo,
};

// Switch Icons

const toSvgXml = (content: string) => `url('data:image/svg+xml;utf8,${content}')`;

const cancelSvg = `<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 18 18"><path fill="${encodeURIComponent(
  '#1D2026',
)}" d="M14.25 4.8075L13.1925 3.75L9 7.9425L4.8075 3.75L3.75 4.8075L7.9425 9L3.75 13.1925L4.8075 14.25L9 10.0575L13.1925 14.25L14.25 13.1925L10.0575 9L14.25 4.8075Z"/></svg>`;

const checkSvg = `<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 18 18"><path fill="${encodeURIComponent(
  '#fff',
)}" d="M6.75109 12.1284L3.62359 9.00086L2.55859 10.0584L6.75109 14.2509L15.7511 5.25086L14.6936 4.19336L6.75109 12.1284Z"/></svg>`;

export const theme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        ${inspire}
        ${inspireMed}
        ${inspireBlack}
        ${inspireHeavy}

        a {
          color: inherit;
          text-decoration: underline;
        }

        body *::-webkit-scrollbar {
          -webkit-appearance: none;
        }

        body *::-webkit-scrollbar:vertical {
          width: 12px;
        }

        body *::-webkit-scrollbar:horizontal {
          height: 12px;
        }

        body *::-webkit-scrollbar-thumb {
          border-radius: 8px;
          border: 2px solid white;
          background-color: #A9A9A9;
        }
    `,
    },
    MuiSvgIcon: {
      variants: [
        {
          props: { fontSize: 'xs' },
          style: {
            fontSize: iconSizes.xs,
          },
        },
      ],
    },
    MuiList: {
      styleOverrides: {
        root: {
          '&.two-col-select': {
            width: '368px',
          },
          '&.two-col-select .column': {
            width: '43%',
            float: 'left',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: '16px',
          paddingBottom: '16px',
          paddingRight: '16px',
          paddingLeft: '16px',
          margin: '0 16px 1px',
          borderBottom: 'none',

          '&.Mui-focused': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-focusVisible': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'transparent',
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.last': {
            borderBottom: '0',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          whiteSpace: 'initial',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(0,0,0,0.87)',
          '&.Mui-focused': {
            color: 'rgba(0,0,0,0.87)',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: '#6C696A', // Primary/Shade/40
        },
        thumb: {
          backgroundImage: toSvgXml(cancelSvg),
        },
        switchBase: {
          '&.Mui-checked ': {
            '& + .MuiSwitch-track': {
              backgroundColor: 'rgba(10, 92, 51, 0.5)', // success/outlinedBorder
            },
            '& .MuiSwitch-thumb': {
              backgroundImage: toSvgXml(checkSvg),
              color: palette.success.main,
            },
          },
          '&.Mui-disabled .MuiSwitch-thumb': {
            backgroundImage: 'none',
            color: 'unset',
          },
          '&.Mui-disabled': {
            color: '#B5B4B4', // Primary/Shade/20
            '&.Mui-checked': {
              color: '#B5B4B4', // Primary/Shade/20
            },
            '& + .MuiSwitch-track': {
              opacity: 1,
              backgroundColor: 'rgba(35, 31, 32, 0.26)',
            },
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: 'rgba(0,0,0,0.87)',
          '&.Mui-checked': {
            color: palette.primary.main,
          },
          '&.MuiRadio-colorSecondary.Mui-checked': {
            color: palette.primary.main,
          },
          '&:hover': {
            background: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontWeight: 400,
        },
        label: {
          paddingLeft: '12px',
          paddingRight: '12px',
        },
        labelSmall: {
          paddingLeft: '8px',
          paddingRight: '8px',
        },
        colorPrimary: {
          color: palette.primary.main,
          border: `1px solid ${palette.primary.main}`,

          '&.MuiChip-filledPrimary': {
            backgroundColor: palette.action.active,
            color: 'white',
            border: `1px solid ${palette.action.active}`,
          },
        },
        iconSmall: {
          marginRight: '8px',
          marginLeft: 0,
        },
        colorInfo: {
          color: palette.info.main,
          border: `1px solid ${palette.info.main}`,

          '&.MuiChip-filledInfo': {
            backgroundColor: palette.info.main,
            color: 'white',
            border: `1px solid ${palette.info.main}`,
          },
        },
        colorSuccess: {
          color: palette.success.main,
          border: `1px solid ${palette.success.main}`,

          '&.MuiChip-filledSuccess': {
            backgroundColor: palette.success.main,
            color: 'white',
            border: `1px solid ${palette.success.main}`,
          },
        },
        colorWarning: {
          color: palette.warning.main,
          border: `1px solid ${palette.warning.main}`,

          '&.MuiChip-filledWarning': {
            backgroundColor: palette.warning.main,
            color: 'white',
            border: `1px solid ${palette.warning.main}`,
          },
        },
        colorError: {
          color: palette.error.main,
          border: `1px solid ${palette.error.main}`,

          '&.MuiChip-filledError': {
            backgroundColor: palette.error.main,
            color: 'white',
            border: `1px solid ${palette.error.main}`,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #DBDBDD',
          marginBottom: '1.5rem',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '1.5rem 0',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          '&[aria-selected="true"]': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
          '&:active': {
            backgroundColor: '#f5f5f5',
          },
        },
        listbox: {
          '&::-webkit-scrollbar': {
            '-webkit-appearance': 'none',
            '&:vertical': {
              width: '12px',
            },
            '&:horizontal': {
              height: '12px',
            },
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '8px',
            border: '2px solid white',
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
          },
        },
        clearIndicator: {
          '&:hover': {
            cursor: 'pointer',
          },
        },
      },
    },
    MuiGridLegacy: {
      styleOverrides: {
        'spacing-xs-2': {
          width: 'calc(100% + 16px)',
          margin: '-8px',

          '> .MuiGridLegacy-item': {
            padding: '8px',
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: 'rgba(35, 31, 32, 0.6)',
            opacity: 1,
          },
        },
        root: {
          '& textarea::placeholder': {
            color: 'rgba(35, 31, 32, 0.6)',
            opacity: 1,
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          '&.MuiFormLabel': {
            color: 'rgba(0,0,0,0.87)',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          lineHeight: '1em',

          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -6px) scale(0.75)',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: palette.info.main,
        },
      },
    },
    MuiTimelineItem: {
      styleOverrides: {
        root: {
          ':before': {
            flex: '0',
            padding: '0',
          },
        },
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: {
          padding: '32px 0 0',

          '& .MuiContainer-root': {
            paddingTop: 0,
            marginTop: 0,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          padding: 0,
          minHeight: 0,

          '& .MuiTabs-indicator': {
            height: '3px',
            backgroundColor: lightTokens.primary.main,
          },
        },
      },
    },
    MuiTab: {
      defaultProps: {
        TouchRippleProps: { center: true },
      },
      styleOverrides: {
        root: {
          fontSize: '1rem',
          padding: '9px 16px',
          minWidth: 0,
          minHeight: 0,
          fontWeight: 700,
          textTransform: 'none',
          lineHeight: 1,
          margin: '0 12px 0 0',

          '&:hover': {
            color: 'rgba(35, 31, 32, 1)',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: 'rgba(29, 32, 38, 0.9)',
          borderRadius: '4px',
          color: '#FFFFFF',
          fontSize: '.75rem',
          maxWidth: '300px',
        },
        popper: {
          '&[data-popper-placement*="bottom"] .MuiTooltip-tooltip.MuiTooltip-tooltipPlacementBottom': {
            marginTop: '3px',
          },
          '&[data-popper-placement*="top"] .MuiTooltip-tooltip.MuiTooltip-tooltipPlacementTop': {
            marginBottom: '3px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          letterSpacing: '1.25px',
          textTransform: 'capitalize',
          fontSize: '0.875rem',
        },
        sizeSmall: {
          paddingLeft: '8px',
          paddingRight: '8px',
        },
        outlinedPrimary: {
          borderColor: palette.primary.main,
        },
        outlinedError: {
          borderColor: palette.error.main,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: '#6C696A',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '&.changesRequested .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: palette.changeRequested.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: palette.changeRequested.main,
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          textTransform: 'none',

          '&.Mui-error': {
            color: palette.error.main,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.info.main,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.error.main, // palette.error.main
          },
          '&:hover .button': {
            borderColor: palette.primary.main,
          },
        },
        notchedOutline: {
          '&.changesRequested .MuiOutlinedInput-root': {
            borderColor: palette.changeRequested.main,
          },

          '&.changesRequested.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.changeRequested.main,
          },
        },
      },
    },
    MuiTimelineDot: {
      styleOverrides: {
        root: {
          margin: '8px 0',
        },
      },
    },
    MuiTimelineConnector: {
      styleOverrides: {
        root: {
          width: '1px',
          backgroundColor: 'rgba(35, 31, 32, 0.12)',
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        rowHeight: 78,
        disableRowSelectionOnClick: true,
        disableColumnFilter: true,
        isCellEditable: () => false,
      },
      styleOverrides: {
        root: { border: '0' },
        main: {
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: ' #B7B7BC',
          borderRadius: '8px 8px 0 0',
          marginTop: '32px',
        },
        footerContainer: {
          borderWidth: '0 1px 1px',
          borderStyle: 'solid',
          borderColor: ' #B7B7BC',
          borderRadius: '0 0 8px 8px',
          '&.MuiDataGrid-withBorderColor': {
            borderColor: ' #B7B7BC',
          },
        },
        toolbar: { border: '0', padding: '0' },
        row: {
          borderColor: 'rgba(35, 31, 32, 0.12)',
          '&:hover': {
            background: 'transparent',
          },
        },
        columnHeader: {
          border: 'none',
          padding: '0 16px',
          '&:focus': {
            outline: 'none',
          },
          '&:focus-within': {
            outline: 'none',
          },
        },
        columnHeaderTitle: {
          fontSize: '0.875rem',
          lineHeight: '20px',
          fontWeight: 700,
        },
        columnSeparator: {
          display: 'none',
        },
        cell: {
          padding: '0 16px',
          '&:focus': {
            outline: 'none',
          },
          '&:focus-within': {
            outline: 'none',
          },
        },
        menu: {
          color: palette.text.primary,
          padding: 0,
          '& .MuiSvgIcon-root': {
            color: palette.text.primary,
          },
          '& li.MuiButtonBase-root': {
            margin: 0,
            padding: '4px 8px 4px 16px',
          },
          '& .MuiListItemText-root': {
            textTransform: 'capitalize',
          },
        },
        panelWrapper: {
          '& .MuiDataGrid-columnsManagementHeader': {
            borderBottom: 0,
            '&. MuiInputBase-root': {
              height: '33px',
            },
          },
          '& .MuiDataGrid-columnsManagement': {
            padding: 0,
            '& .MuiFormControlLabel-root': {
              margin: 0,
              height: '48px',
              padding: '0 16px',
              '& .MuiFormControlLabel-label': {
                fontSize: '1rem',
              },

              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 320,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  palette,
  spacingTokens,
  typography: {
    fontFamily: 'Inspire, Helvetica Neue, Arial, sans-serif',
    h2: {
      fontSize: '3.75rem',
      fontWeight: 800,
      lineHeight: 1.2,
    },
    h3: {
      fontSize: '3rem',
      fontStyle: 'normal',
      fontWeight: 800,
      lineHeight: 1.2,
      textAlign: 'left',
    },
    h4: {
      fontSize: '2.125rem',
      fontStyle: 'normal',
      fontWeight: 800,
      lineHeight: 1.2,
      textAlign: 'left',
    },
    h5: {
      fontSize: '1.5rem',
      fontStyle: 'normal',
      fontWeight: 800,
      lineHeight: 'normal',
      letterSpacing: '0.18px',
      textAlign: 'left',
    },
    h6: {
      fontSize: '1.25rem',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '1.2',
      letterSpacing: '0.15px',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.429,
      letterSpacing: 0,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 800,
      lineHeight: '1.5',
      letterSpacing: '0.15px',
      textTransform: 'none',
    },
    subtitle2: {
      fontFamily: '"Inspire", "Helvetica Neue", Arial, sans-serif',
      fontSize: '0.875rem',
      lineHeight: '20px',
      fontWeight: 600,
    },
    overline: {
      fontSize: '0.625rem',
      letterSpacing: '1.6px',
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: '1.2',
      letterSpacing: '0.4px',
    },
    button: {
      fontSize: '.875rem',
      textTransform: 'uppercase',
    },
  },
});
